import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useMemo } from "react";

import { oktaConfig } from "src/config/okta.config";
import { REACT_QUERY_STALE_TIME } from "src/constants";
import i18n from "src/i18n";
import { useAuthStore } from "src/stores/auth-store";
import { type PasswordComplexity } from "src/utils";

type OktaPasswordComplexityResponse = {
  complexity: PasswordComplexity;
  description?: string;
};

type UsePasswordComplexityRulesResult = {
  isLoading: boolean;
  passwordComplexity: PasswordComplexity | undefined;
};

const cacheKeys = {
  passwordComplexity: (token?: string) => ["okta", "password", "complexity", token] as const,
};

async function fetchOktaPasswordComplexity(token: string): Promise<OktaPasswordComplexityResponse> {
  const response = await fetch(
    `${oktaConfig.baseUri}/idp/myaccount/password/complexity-requirements`,
    {
      method: "GET",
      headers: {
        Accept: "application/json; okta-version=1.0.0",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const errorMessage = await response.text().catch(() => null);
    throw new Error(
      errorMessage ||
        i18n.t(
          "Une erreur est survenue lors de la récupération des règles de complexité du mot de passe."
        )
    );
  }

  return response.json();
}

export const usePasswordComplexityRules = (
  options: UseQueryOptions<OktaPasswordComplexityResponse, Error> = {}
): UsePasswordComplexityRulesResult => {
  const token = useAuthStore((state) => state.token) ?? undefined;

  const { data: passwordComplexityData, isLoading, fetchStatus } = useQuery<
    OktaPasswordComplexityResponse,
    Error
  >({
    queryKey: cacheKeys.passwordComplexity(token),
    queryFn: async () => {
      if (!token) {
        throw new Error("Not authenticated");
      }
      return fetchOktaPasswordComplexity(token);
    },
    staleTime: REACT_QUERY_STALE_TIME,
    ...options,
  });

  const passwordComplexity = useMemo(() => passwordComplexityData?.complexity, [
    passwordComplexityData,
  ]);

  return {
    isLoading: fetchStatus === "fetching" && isLoading,
    passwordComplexity,
  };
};
